import React, { useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaClock, FaCalendarAlt, FaUsers, FaStar, FaCheckCircle, FaArrowRight,
  FaBookOpen, FaCertificate, FaTrophy, FaGlobe, FaAward, FaClipboardCheck,
  FaComments, FaUserMd, FaChartLine, FaCheck, FaStethoscope, FaExternalLinkAlt, FaListUl
} from 'react-icons/fa';

/* ── tokens ── */
const T = {
  navy:       '#1a1a2e',
  indigo:     '#3730A3',
  indigoLight:'#f5f3ff',
  accent:     '#a78bfa',
  muted:      '#4a4a6a',
  border:     '#e8e6f0',
  bg:         '#ffffff',
  serif:      "'Times New Roman', serif",
};

/* ══════════════════════════════════════════
   GLOBAL
══════════════════════════════════════════ */
const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

/* ══════════════════════════════════════════
   PAGE WRAPPER
══════════════════════════════════════════ */
const PageContainer = styled.div`
  margin-top: 195px;
  background: ${T.bg};
  font-family: ${T.serif};
  color: ${T.navy};

  @media (max-width: 1366px) and (min-width: 769px) { margin-top: 158px; }
  @media (max-width: 768px) { margin-top: 120px; }
`;

/* ══════════════════════════════════════════
   HERO
══════════════════════════════════════════ */
const Hero = styled.div`
  background: linear-gradient(160deg, #0b0b77 0%, #1a1050 30%, #3730A3 60%, #0b0b77 100%);
  display: flex;
  align-items: center;
  min-height: 300px;
  padding: 3.5rem 5%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${T.accent} 0%, ${T.indigo} 100%);
  }

  &::after {
    content: '';
    position: absolute;
    right: -60px;
    top: -60px;
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, rgba(167, 139, 250, 0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    min-height: 250px;
    padding: 2.5rem 5%;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 2.5rem 5%;
    min-height: auto;
    
    &::before { width: 100%; height: 4px; top: 0; left: 0; right: 0; bottom: auto; }
  }
`;

const HeroWatermark = styled.div`
  position: absolute;
  right: 3%;
  top: 50%;
  transform: translateY(-50%);
  font-family: ${T.serif};
  font-size: 180px;
  font-weight: 900;
  color: rgba(255,255,255,0.06);
  pointer-events: none;
  user-select: none;
  letter-spacing: -10px;
  line-height: 1;
  z-index: 1;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 130px;
    right: 4%;
    color: rgba(255,255,255,0.06);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  flex: 1;
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(167, 139, 250, 0.1);
  border: 1px solid rgba(167, 139, 250, 0.25);
  color: ${T.accent};
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 0.4rem 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  font-weight: 700;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: ${T.accent};
    border-radius: 50%;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.7rem;
    margin-bottom: 1rem;
  }
`;

const HeroTitle = styled.h1`
  font-family: ${T.serif};
  font-size: 3.2rem;
  font-weight: 900;
  color: #fff;
  line-height: 1.1;
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 2.5rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-family: ${T.serif};
  font-size: 1.1rem;
  color: rgba(255,255,255,0.6);
  font-style: italic;
  margin-bottom: 1.8rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.95rem;
    margin-bottom: 1.2rem;
  }
`;

const HeroStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const StatPill = styled.div`
  font-family: ${T.serif};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.6rem 1.2rem;
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 6px;
  background: rgba(255,255,255,0.04);
  min-width: 100px;

  .val { 
    font-size: 1.3rem; 
    font-weight: 900; 
    color: ${T.accent}; 
    line-height: 1;
  }
  .lbl { 
    font-size: 0.7rem; 
    color: rgba(255,255,255,0.5); 
    text-transform: uppercase; 
    letter-spacing: 1px; 
    margin-top: 4px; 
    font-weight: 600;
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 0.5rem 1rem;
    min-width: 85px;
    .val { font-size: 1.1rem; }
    .lbl { font-size: 0.62rem; }
  }
`;

/* ══════════════════════════════════════════
   WRAP
══════════════════════════════════════════ */
const Wrap = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1.2rem;
  }
`;

/* ══════════════════════════════════════════
   SECTION RULE
══════════════════════════════════════════ */
const SecRule = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin: 3rem 0 1.5rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    margin: 2.5rem 0 1.2rem;
  }
`;

const RuleDot = styled.div`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: ${T.indigo};
  flex-shrink: 0;
`;

const RuleLabel = styled.span`
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: ${T.indigo};
  font-weight: 700;
  white-space: nowrap;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.7rem;
  }
`;

const RuleLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${T.border};
`;

/* ══════════════════════════════════════════
   TWO-PANEL
══════════════════════════════════════════ */
const TwoPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const TopicPanel = styled.div`
  background: #fff;
  border: 1px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;
`;

const PanelHead = styled.div`
  background: ${T.navy};
  padding: 0.85rem 1.5rem;
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.8);
  font-weight: 700;
`;

const TopicItem = styled.div`
  font-family: ${T.serif};
  display: flex;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 0.7rem 1.5rem;
  border-bottom: 1px solid ${T.border};
  font-size: 0.95rem;
  color: ${T.muted};

  &:last-child { border-bottom: none; }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.88rem;
    padding: 0.6rem 1.2rem;
  }
`;

const TopicNum = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${T.indigo};
  min-width: 28px;
  padding-top: 2px;
  font-style: italic;
`;

const BenefitPanel = styled.div`
  background: ${T.indigo};
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const BenefitLabel = styled.div`
  font-family: ${T.serif};
  font-size: 0.75rem;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.6);
  font-weight: 700;
  margin-bottom: 1rem;
`;

const BenefitItem = styled.div`
  font-family: ${T.serif};
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  font-size: 0.95rem;
  color: rgba(255,255,255,0.9);
  line-height: 1.5;

  &:last-child { border-bottom: none; }

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.85rem;
  }
`;

const BenefitCheck = styled.div`
  width: 20px; height: 20px;
  background: rgba(167, 139, 250, 0.2);
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  color: ${T.accent};
  font-size: 11px;
`;

/* ══════════════════════════════════════════
   QUERY BOX
══════════════════════════════════════════ */
const QueryBox = styled.div`
  font-family: ${T.serif};
  background: ${T.indigoLight};
  border-radius: 8px;
  padding: 1.2rem 1.5rem;
  border-left: 4px solid ${T.indigo}; 
  font-style: italic; 
  color: ${T.muted}; 
  font-size: 0.95rem; 
  line-height: 1.7;
`;

/* ══════════════════════════════════════════
   BENEFIT CARDS GRID
══════════════════════════════════════════ */
const BenefitCardGrid = styled.div`
  display: grid; 
  grid-template-columns: repeat(3, 1fr); 
  gap: 1px;
  background: ${T.border};
  border: 1px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;
  
  @media (max-width: 800px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 500px) { grid-template-columns: 1fr; }
`;

const BenefitCardItem = styled.div`
  font-family: ${T.serif};
  background: ${({ $odd }) => $odd ? T.indigoLight : '#fff'};
  padding: 1.2rem 1.3rem;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover { 
    background: ${({ $odd }) => $odd ? '#ede8ff' : '#fafbff'};
  }
  
  .benefit-icon { 
    font-size: 1.5rem; 
    color: ${T.indigo}; 
    margin-bottom: 0.5rem; 
  }
  
  h4 { 
    font-size: 0.95rem; 
    color: ${T.navy}; 
    margin-bottom: 0.3rem; 
    font-weight: 800;
  }
  
  p { 
    font-size: 0.82rem; 
    color: ${T.muted}; 
    line-height: 1.5; 
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    padding: 1rem 1.1rem;
    h4 { font-size: 0.85rem; }
    p { font-size: 0.75rem; }
  }
`;

/* ══════════════════════════════════════════
   CERT BLOCK
══════════════════════════════════════════ */
const CertBlock = styled.div`
  background: #fff;
  border: 1px solid ${T.border};
  border-radius: 8px;
  overflow: hidden;
`;

const CertHead = styled.div`
  background: ${T.indigo};
  padding: 1.2rem 1.8rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const CertHeadTitle = styled.div`
  font-family: ${T.serif};
  font-size: 1.1rem;
  color: #fff;
  font-weight: 800;
`;

const CertHeadBadge = styled.div`
  font-family: ${T.serif};
  background: rgba(167, 139, 250, 0.2);
  color: ${T.accent};
  border: 1px solid rgba(167, 139, 250, 0.3);
  font-size: 0.72rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  font-weight: 700;
`;

const CertBody = styled.div`
  padding: 1.8rem;
`;

const CertDesc = styled.p`
  font-family: ${T.serif};
  font-size: 0.95rem;
  color: ${T.muted};
  line-height: 1.8;
  margin-bottom: 1.8rem;
  border-left: 3px solid ${T.accent};
  padding-left: 1.2rem;

  @media (max-width: 1366px) and (min-width: 769px) {
    font-size: 0.88rem;
  }
`;

const CertCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.8rem;

  @media (max-width: 650px) { grid-template-columns: 1fr; }
`;

const CertColTitle = styled.div`
  font-family: ${T.serif};
  font-size: 0.78rem;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${T.indigo};
  font-weight: 700;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PlainList = styled.ul`
  list-style: none;
  padding: 0;

  li {
    font-family: ${T.serif};
    font-size: 0.92rem;
    color: ${T.muted};
    padding: 0.35rem 0;
    border-bottom: 1px solid ${T.border};
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    line-height: 1.5;

    &:last-child { border-bottom: none; }

    svg { color: ${T.indigo}; font-size: 0.8rem; margin-top: 3px; flex-shrink: 0; }
  }

  @media (max-width: 1366px) and (min-width: 769px) {
    li { font-size: 0.85rem; }
  }
`;

const FeeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${T.serif};
  margin-bottom: 1rem;

  th {
    background: ${T.navy};
    color: rgba(255,255,255,0.7);
    font-size: 0.75rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    padding: 0.7rem 1.2rem;
    text-align: left;
    font-weight: 700;
  }

  td {
    padding: 0.9rem 1.2rem;
    border-bottom: 1px solid ${T.border};
    font-size: 0.95rem;
    color: ${T.muted};
  }

  tr:last-child td { border-bottom: none; }

  .amt {
    font-size: 1.5rem;
    font-weight: 900;
    color: ${T.navy};
  }

  tr.highlight { background: ${T.indigoLight}; }
  tr.highlight .amt { color: ${T.indigo}; }
  tr.highlight td { border-bottom: none; }
`;

const FeeNoteTag = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  background: rgba(55,48,163,0.1);
  color: ${T.indigo};
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  margin-left: 0.5rem;
  vertical-align: middle;
  font-weight: 700;
`;

const CertLinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding-top: 0.8rem;
  border-top: 1px solid ${T.border};

  a {
    font-family: ${T.serif};
    color: ${T.indigo};
    font-size: 0.92rem;
    font-weight: 700;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }

  svg { color: ${T.indigo}; font-size: 0.85rem; }
`;

/* ══════════════════════════════════════════
   CTA
══════════════════════════════════════════ */
const CTASection = styled.div`
  margin: 3rem 0 1rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: ${T.navy};
  border-radius: 8px;
  overflow: hidden;
  padding: 1.5rem 1.5rem 1.5rem 0;

  &::before {
    content: '';
    width: 5px;
    align-self: stretch;
    background: ${T.accent};
    flex-shrink: 0;
  }

  @media (max-width: 650px) {
    flex-direction: column;
    padding: 1.5rem;
    &::before { display: none; }
  }
`;

const CTAText = styled.div`
  flex: 1;
  .cta-title {
    font-family: ${T.serif};
    font-size: 1.4rem;
    font-weight: 800;
    color: #fff;
    margin-bottom: 0.3rem;
  }
  .cta-sub {
    font-family: ${T.serif};
    font-size: 0.95rem;
    color: rgba(255,255,255,0.55);
    font-style: italic;
  }
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${T.accent};
  color: ${T.navy};
  font-family: ${T.serif};
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.8rem 1.8rem;
  border-radius: 50px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover { 
    background: #c4b5fd; 
    transform: translateY(-2px);
  }
`;

const Spacer = styled.div`
  height: 2rem;
`;

/* ══════════════════════════════════════════
   DATA
══════════════════════════════════════════ */
const impactAreas = [
  'Accurate DRG assignment for proper reimbursement',
  'HCC capture and RAF score optimization',
  'Quality measures and value-based care reporting',
  'Reduced audit risk and improved compliance',
  'Better patient outcomes through complete documentation',
  'Improved physician communication and engagement',
];

const benefits = [
  { icon: <FaStethoscope />, title: "Clinical Knowledge", desc: "Understand disease processes and clinical indicators to identify documentation gaps." },
  { icon: <FaComments />, title: "Query Writing Skills", desc: "Master compliant, non-leading queries that improve documentation specificity." },
  { icon: <FaChartLine />, title: "DRG & HCC Impact", desc: "Learn how CDI impacts DRG assignment and HCC capture for optimal reimbursement." },
  { icon: <FaUserMd />, title: "Physician Engagement", desc: "Build relationships with physicians through effective communication strategies." },
  { icon: <FaClipboardCheck />, title: "Compliance Focus", desc: "Ensure documentation meets regulatory requirements and reduces audit risk." },
  { icon: <FaTrophy />, title: "Career Growth", desc: "CDI specialists earn $70K-$95K annually with excellent advancement opportunities." }
];

const certDetails = [
  { title: "CDIP Exam Coverage", items: ["Clinical documentation standards", "Regulatory requirements", "Query development", "DRG and HCC impact"] },
  { title: "CCDS Exam Coverage", items: ["Clinical concepts", "Quality measures", "CDI program management", "Compliance and ethics"] }
];

const inclusions = [
  '60 Hours Expert Training',
  'Study Materials & Practice Exercises',
  'Mock Exams (CCDS/CDIP Pattern)',
  'One-on-One Mentoring',
  'Query Writing Practice',
  'Certificate of Completion',
];

/* ══════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════ */
const CDI = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <GlobalStyle />
      <PageContainer>

        {/* ── HERO ── */}
        <Hero>
          <HeroWatermark>CDI</HeroWatermark>
          <HeroContent>
            <HeroTag><FaAward /> India's First AHIMA Approved CDI Trainer</HeroTag>
            <HeroTitle>Clinical Documentation Improvement (CDI)</HeroTitle>
            <HeroSubtitle>Training provided by AHIMA Certified Trainer Gaayatri Jayapal </HeroSubtitle>
            
          </HeroContent>
        </Hero>

        <Wrap>

          {/* ── WHAT IS CDI + CDI IMPACT ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Understanding CDI</RuleLabel><RuleLine />
          </SecRule>
          <TwoPanel>
            <TopicPanel>
              <PanelHead>What is CDI?</PanelHead>
              <TopicItem>
                <span>
                  <strong>Clinical Documentation Improvement (CDI)</strong> is the process of reviewing 
                  medical records for completeness and accuracy, then querying physicians to clarify or 
                  add documentation. CDI specialists ensure the medical record accurately reflects the 
                  patient's clinical status and care provided.
                </span>
              </TopicItem>
              <TopicItem>
                <span>
                  At Learn Thru Us Academy, we're proud to have the <strong>first trainer from India 
                  recognized for CDI training</strong>. Our founder, Gaayatri Jayapal, is an AHIMA 
                  Approved CDI Trainer with decades of experience.
                </span>
              </TopicItem>
            </TopicPanel>

            <BenefitPanel>
              <BenefitLabel>CDI Impact Areas</BenefitLabel>
              {impactAreas.map((item, i) => (
                <BenefitItem key={i}>
                  <BenefitCheck><FaCheck /></BenefitCheck>
                  {item}
                </BenefitItem>
              ))}
            </BenefitPanel>
          </TwoPanel>

          {/* ── SAMPLE QUERY ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Sample CDI Query</RuleLabel><RuleLine />
          </SecRule>
          <QueryBox>
            "The patient has a diagnosis of pneumonia documented. However, the specific organism 
            and whether this is community-acquired or healthcare-associated pneumonia are not 
            specified. Please clarify the type of pneumonia and the causative organism if known. 
            This documentation will support accurate coding and quality reporting."
          </QueryBox>

          {/* ── BENEFITS ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Why Our CDI Training Stands Out</RuleLabel><RuleLine />
          </SecRule>
          <BenefitCardGrid>
            {benefits.map((b, i) => (
              <BenefitCardItem key={i} $odd={i % 2 !== 0}>
                <div className="benefit-icon">{b.icon}</div>
                <h4>{b.title}</h4>
                <p>{b.desc}</p>
              </BenefitCardItem>
            ))}
          </BenefitCardGrid>

          {/* ── CERTIFICATION ── */}
          <SecRule>
            <RuleDot /><RuleLabel>Certification &amp; Pricing</RuleLabel><RuleLine />
          </SecRule>
          <CertBlock>
            <CertHead>
              <CertHeadTitle>CDI Certification — AHIMA</CertHeadTitle>
              <CertHeadBadge>Globally Recognized</CertHeadBadge>
            </CertHead>
            <CertBody>
              <CertDesc>
                This intensive program prepares you for both the <strong>CCDS (AHIMA)</strong> and{' '}
                <strong>CDIP (AHIMA)</strong> certification exams. You'll learn CDI principles, 
                query writing, DRG and HCC impact, and quality measures.
              </CertDesc>

              <CertCols>
                <div>
                  <CertColTitle><FaCertificate /> Exam Coverage</CertColTitle>
                  <PlainList>
                    {certDetails[0].items.map((item, j) => <li key={j}><FaCheck />{item}</li>)}
                    {certDetails[1].items.map((item, j) => <li key={j}><FaCheck />{item}</li>)}
                  </PlainList>
                </div>
                <div>
                  <CertColTitle><FaListUl /> Course Inclusions</CertColTitle>
                  <PlainList>
                    {inclusions.map((inc, i) => (
                      <li key={i}><FaCheck />{inc}</li>
                    ))}
                  </PlainList>
                </div>
              </CertCols>

              <FeeTable>
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Description</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>AHIMA Membership</strong></td>
                    <td>To be paid to AHIMA directly</td>
                    <td><span className="amt">$79</span></td>
                  </tr>
                  <tr>
                    <td><strong>CDIP Exam</strong></td>
                    <td>Certification examination fee to be paid to AHIMA directly</td>
                    <td><span className="amt">$299</span></td>
                  </tr>
                  <tr className="highlight">
                    <td><strong>Our Course Fee</strong></td>
                    <td>To be paid to LearnThruUs Academy </td>
                    <td><span className="amt">₹30,000 + GST</span></td>
                  </tr>
                </tbody>
              </FeeTable>

              <CertLinkRow>
                <FaExternalLinkAlt />
                <a
                  href="http://www.ahima.org/certification/CDIP"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit AHIMA for full certification details
                </a>
              </CertLinkRow>
            </CertBody>
          </CertBlock>

          {/* ── CTA ── */}
          <CTASection>
            <CTAText>
              <div className="cta-title">Ready to Become a CDI Specialist?</div>
              <div className="cta-sub">Join our comprehensive CDI training and transform healthcare documentation.</div>
            </CTAText>
            <CTAButton to="/contact-us">
              Enroll Now <FaArrowRight />
            </CTAButton>
          </CTASection>

          <Spacer />
        </Wrap>
      </PageContainer>
    </>
  );
};

export default CDI;